import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiShoppingCart, FiMenu, FiX, FiUser, FiLogOut, FiSearch, FiChevronDown, FiPhone, FiPackage, FiGrid, FiCheckCircle, FiSettings, FiHeart } from 'react-icons/fi';
import { BsGrid, BsLightbulb, BsToggles, BsPlugin, BsFan, BsBatteryCharging, BsTools, BsOutlet } from 'react-icons/bs';
import { MdElectricalServices, MdWaterDrop, MdKitchen } from 'react-icons/md';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import toast from 'react-hot-toast';
import './Header.css';

function Header() {
  const { isAuthenticated, user, logout, isAdmin, loading } = useAuth();
  const { cart = { itemCount: 0 } } = useCart();
  const { wishlistCount } = useWishlist();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setProfileOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    setLoggingOut(true);
    setProfileOpen(false);
    // Brief pause so the user sees the "Signing out..." state before clearing
    await new Promise((r) => setTimeout(r, 400));
    await logout();
    toast.success('Signed out successfully');
    navigate('/');
    setLoggingOut(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setMenuOpen(false);
    }
  };

  const isActive = (href) => {
    const [pathname, query] = href.split('?');
    if (location.pathname !== pathname) return false;
    if (!query) {
      // "All Products" / "Home" etc — active only when no category filter is set
      return !new URLSearchParams(location.search).get('category');
    }
    // Category link — active when the category param matches exactly
    const hrefCategory = new URLSearchParams(query).get('category');
    const locCategory  = new URLSearchParams(location.search).get('category');
    return hrefCategory === locCategory;
  };

  const getRoleBadge = (role) => {
    const badges = { admin: { label: 'Admin', color: '#dc2626' }, dealer: { label: 'Dealer', color: '#7c3aed' }, electrician: { label: 'Electrician', color: '#059669' }, engineer: { label: 'Engineer', color: '#2563eb' }, customer: { label: 'Customer', color: '#6b7280' } };
    return badges[role] || badges.customer;
  };

  const navCategories = [
    { href: '/shop', label: 'All Products', shortLabel: 'All', icon: <BsGrid size={14} />, mobileIcon: <BsGrid size={22} />, color: '#6366f1' },
    { href: '/shop?category=LED_LIGHTS', label: 'LED Lights', shortLabel: 'Lights', icon: <BsLightbulb size={14} />, mobileIcon: <BsLightbulb size={22} />, color: '#f59e0b' },
    { href: '/shop?category=SWITCHES', label: 'Switches', shortLabel: 'Switches', icon: <BsToggles size={14} />, mobileIcon: <BsToggles size={22} />, color: '#10b981' },
    { href: '/shop?category=WIRES_CABLES', label: 'Wires & Cables', shortLabel: 'Wires', icon: <BsPlugin size={14} />, mobileIcon: <MdElectricalServices size={22} />, color: '#ef4444' },
    { href: '/shop?category=FANS', label: 'Fans', shortLabel: 'Fans', icon: <BsFan size={14} />, mobileIcon: <BsFan size={22} />, color: '#3b82f6' },
    { href: '/shop?category=INVERTER', label: 'Inverters & UPS', shortLabel: 'Inverters', icon: <BsBatteryCharging size={14} />, mobileIcon: <BsBatteryCharging size={22} />, color: '#8b5cf6' },
    { href: '/shop?category=TOOLS', label: 'Tools', shortLabel: 'Tools', icon: <BsTools size={14} />, mobileIcon: <BsTools size={22} />, color: '#f97316' },
    { href: '/shop?category=PLUGS_SOCKETS', label: 'Plugs & Sockets', shortLabel: 'Plugs', icon: <BsOutlet size={14} />, mobileIcon: <BsOutlet size={22} />, color: '#14b8a6' },
    { href: '/shop?category=WATER_HEATERS', label: 'Water Heaters', shortLabel: 'Heaters', icon: <MdWaterDrop size={14} />, mobileIcon: <MdWaterDrop size={22} />, color: '#0ea5e9' },
    { href: '/shop?category=APPLIANCES', label: 'Appliances', shortLabel: 'Appliances', icon: <MdKitchen size={14} />, mobileIcon: <MdKitchen size={22} />, color: '#ec4899' },
    { href: '/services', label: 'Services', shortLabel: 'Services', icon: <FiSettings size={14} />, mobileIcon: <FiSettings size={22} />, color: '#64748b' },
  ];

  return (
    <>
      {/* ===== MAIN HEADER ===== */}
      <header className={`header ${scrolled ? 'header-scrolled' : ''}`}>
        <div className="header-inner">
          {/* Logo */}
          <Link to="/" className="logo-link" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
            <img src="/emblem-header.png" alt="Ceedees Electricals" style={{ height: '48px', width: '48px', borderRadius: '50%' }} />
            <div style={{ lineHeight: 1.1 }}>
              <span style={{ display: 'block', fontWeight: 900, fontSize: '18px', color: '#fff', letterSpacing: '1px' }}>CEEDEES</span>
              <span style={{ display: 'block', fontWeight: 700, fontSize: '10px', color: 'rgba(255,255,255,0.6)', letterSpacing: '3px' }}>ELECTRICALS</span>
            </div>
          </Link>

          {/* Search */}
          <form className="search-bar" onSubmit={handleSearch}>
            <input
              type="text"
              placeholder=""
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit" aria-label="Search">
              <FiSearch size={16} />
            </button>
          </form>

          {/* Right Actions */}
          <div className="header-actions">
            <a href="tel:+919447672888" className="header-phone" title="Call us">
              <FiPhone size={15} />
              <div>
                <span className="phone-label">Call Us</span>
                <span className="phone-number">+91 94476 72888</span>
              </div>
            </a>

            <div className="action-divider" />

            <Link to="/wishlist" className="action-btn" title="Wishlist">
              <FiHeart size={20} />
              {wishlistCount > 0 && <span className="cart-count wishlist-badge-count">{wishlistCount}</span>}
              <span className="action-text">Wishlist</span>
            </Link>

            <Link to="/cart" className="action-btn" title="Cart">
              <FiShoppingCart size={20} />
              {(cart?.itemCount ?? 0) > 0 && <span className="cart-count">{cart?.itemCount}</span>}
              <span className="action-text">Cart</span>
            </Link>

            {loading ? (
              /* Auth state loading — show subtle skeleton instead of flashing Login */
              <div className="auth-skeleton">
                <div className="auth-skeleton-avatar" />
                <div className="auth-skeleton-text" />
              </div>
            ) : loggingOut ? (
              /* Signing out — show brief feedback */
              <div className="signing-out-badge">
                <div className="signing-out-spinner" />
                <span>Signing out...</span>
              </div>
            ) : isAuthenticated ? (
              <div className="profile-wrap" ref={profileRef}>
                <button className="action-btn profile-btn" onClick={() => setProfileOpen(!profileOpen)}>
                  <div className="avatar avatar-enter">{user.name?.charAt(0).toUpperCase()}</div>
                  <div className="profile-info">
                    <span className="action-text">{user.name?.split(' ')[0]}</span>
                    <FiChevronDown size={13} className={`chev ${profileOpen ? 'up' : ''}`} />
                  </div>
                </button>
                {profileOpen && (
                  <div className="dropdown">
                    <div className="dd-header">
                      <div className="dd-avatar">{user.name?.charAt(0).toUpperCase()}</div>
                      <div>
                        <p className="dd-name">{user.name}</p>
                        <span className="dd-badge" style={{background: getRoleBadge(user.role).color + '15', color: getRoleBadge(user.role).color}}>
                          {getRoleBadge(user.role).label}
                        </span>
                      </div>
                    </div>
                    <div className="dd-sep" />
                    <Link to="/dashboard" className="dd-item"><FiGrid size={15} /> Dashboard</Link>
                    <Link to="/dashboard" className="dd-item"><FiPackage size={15} /> My Orders</Link>
                    {isAdmin && <Link to="/admin" className="dd-item"><FiUser size={15} /> Admin Panel</Link>}
                    <div className="dd-sep" />
                    <button onClick={handleLogout} className="dd-item dd-logout"><FiLogOut size={15} /> Sign Out</button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="login-btn login-btn-enter">
                <FiUser size={16} /> Login
              </Link>
            )}

            <button className="mobile-toggle" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
              {menuOpen ? <FiX size={22} /> : <FiMenu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Search Bar — visible only on mobile */}
        <form className="mobile-search-bar" onSubmit={handleSearch}>
          <FiSearch size={16} className="mobile-search-icon" />
          <input
            type="text"
            placeholder="Search for products, brands..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </form>

        {/* Mobile Category Strip — horizontal scrollable icons */}
        <div className="mobile-cat-strip">
          <div className="mobile-cat-scroll">
            {navCategories.map(link => (
              <Link
                key={link.href}
                to={link.href}
                className={`mobile-cat-item ${isActive(link.href) ? 'mobile-cat-active' : ''}`}
              >
                <div className="mobile-cat-icon" style={{ background: link.color + '15', color: link.color }}>
                  {link.mobileIcon}
                </div>
                <span className="mobile-cat-label">{link.shortLabel}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Desktop Category Nav */}
        <nav className="cat-nav">
          <div className="cat-nav-inner">
            <div className="cat-nav-links">
              {navCategories.map(link => (
                <Link key={link.href} to={link.href} className={`cat-link ${isActive(link.href) ? 'cat-active' : ''}`}>
                  {link.icon} {link.label}
                </Link>
              ))}
            </div>
            <div className="cat-nav-right">
              <span className="gst-tag"><FiCheckCircle size={13} /> GST Registered</span>
              {isAdmin && <Link to="/admin" className="admin-tag">Admin</Link>}
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile Drawer */}
      <div className={`m-overlay ${menuOpen ? 'show' : ''}`} onClick={() => setMenuOpen(false)} />
      <aside className={`m-drawer ${menuOpen ? 'open' : ''}`}>
        <div className="m-drawer-top">
          <Link to="/" className="logo-link" onClick={() => setMenuOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }}>
            <img src="/emblem-header.png" alt="Ceedees Electricals" style={{ height: '40px', width: '40px', borderRadius: '50%' }} />
            <div style={{ lineHeight: 1.1 }}>
              <span style={{ display: 'block', fontWeight: 900, fontSize: '16px', color: '#0f172a', letterSpacing: '1px' }}>CEEDEES</span>
              <span style={{ display: 'block', fontWeight: 700, fontSize: '9px', color: '#64748b', letterSpacing: '3px' }}>ELECTRICALS</span>
            </div>
          </Link>
          <button className="m-close" onClick={() => setMenuOpen(false)}><FiX size={20} /></button>
        </div>
        <form className="m-search" onSubmit={handleSearch}>
          <FiSearch size={16} />
          <input type="text" placeholder="Search products..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </form>
        <nav className="m-nav">
          {navCategories.filter(link => !link.label.includes('LED') && !link.label.includes('Switches') && !link.label.includes('Wires') && !link.label.includes('Fans') && !link.label.includes('Inverters') && !link.label.includes('Tools')).map(link => (
            <Link key={link.href} to={link.href} className="m-link" onClick={() => setMenuOpen(false)}>{link.label}</Link>
          ))}
          <div style={{ textAlign: 'left', padding: '12px 0', fontSize: '13px', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Categories</div>
          {navCategories.filter(link => link.label.includes('LED') || link.label.includes('Switches') || link.label.includes('Wires') || link.label.includes('Fans') || link.label.includes('Inverters') || link.label.includes('Tools')).map(link => (
            <Link key={link.href} to={link.href} className="m-link" onClick={() => setMenuOpen(false)}>{link.label}</Link>
          ))}
          <div className="m-sep" />
          <Link to="/about" className="m-link" onClick={() => setMenuOpen(false)}>About Us</Link>
          <Link to="/contact" className="m-link" onClick={() => setMenuOpen(false)}>Contact</Link>
        </nav>
        <div className="m-bottom">
          {loggingOut ? (
            <div className="m-btn-ghost" style={{ textAlign: 'center', opacity: 0.7 }}>Signing out...</div>
          ) : isAuthenticated ? (
            <>
              <Link to="/dashboard" className="m-btn-fill" onClick={() => setMenuOpen(false)}>My Dashboard</Link>
              <button onClick={handleLogout} className="m-btn-ghost">Sign Out</button>
            </>
          ) : (
            <Link to="/login" className="m-btn-fill" onClick={() => setMenuOpen(false)}>Login / Register</Link>
          )}
        </div>
      </aside>
    </>
  );
}

export default Header;
